import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { ComboBox, FormGroup, InlineNotification, Stack } from '@carbon/react';
import Row from 'src/components/Base/Row';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { FormikRadioButtonGroup } from 'src/components/Form/FormikRadioButtonGroup';
import MoneyInput from 'src/components/Form/MoneyInput';
import { useInventoryCategories } from 'src/hooks/useInventoryCategories';
import { InventoryItemEntity } from 'src/services/apis/types';
import { InventoryItemStatusEnum, InventoryItemStatuses, InventoryUnits } from 'src/utils/inventory';

interface InventoryItemFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: InventoryItemEntity | null | undefined;
}

// unitCost and lowStockThreshold are @IsOptional() on the server (InventoryItemCreateDto),
// so a blank field must submit as omitted, not fail validation. `a == null` (loose) covers
// both `null` and `undefined` — Formik's own validationSchema runner rewrites every ''
// field to `undefined` before calling this schema (prepareDataForValidation in formik's
// core, generic Yup-compatibility plumbing that runs regardless of which adapter — Yup or
// zod-formik-adapter here — supplies the schema), so the preprocess never actually sees ''
// itself; matching only '' | null here let `undefined` fall through to
// `parseFloat('undefined')` -> NaN, which z.number() rejected as "Expected number, received
// nan" on every blank submission.
const BaseSchema = z.object({
  name: z.string().max(120).trim().min(1),
  unitCost: z.preprocess(
    (a) => (a === '' || a == null ? undefined : parseFloat(`${a}`)),
    z.number().nonnegative().optional()
  ),
  lowStockThreshold: z.preprocess(
    (a) => (a === '' || a == null ? undefined : parseFloat(`${a}`)),
    z.number().nonnegative().optional()
  ),
});

const InventoryItemForm = (props: InventoryItemFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();
  const { data: inventoryCategories } = useInventoryCategories();

  useImperativeHandle(ref, () => ({
    submitForm: formikRef.current?.submitForm,
    setFieldValue: formikRef.current?.setFieldValue,
    resetForm: () =>
      formikRef.current?.resetForm({
        values: {
          name: '',
          description: '',
          categoryId: inventoryCategories?.data?.[0]?.id,
          unit: 'kg',
          unitCost: '',
          lowStockThreshold: '',
          status: InventoryItemStatusEnum.ACTIVE,
          tags: '',
        },
      }),
  }));

  return (
    <div id={'inventory-item-form'}>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize={!!props.initialValues}
        validationSchema={toFormikValidationSchema(BaseSchema)}
        initialValues={{
          id: props.initialValues?.id,
          name: props.initialValues?.name || '',
          description: props.initialValues?.description,
          categoryId: props.initialValues?.categoryId || inventoryCategories?.data?.[0]?.id,
          unit: props.initialValues?.unit || 'kg',
          unitCost: props.initialValues?.unitCost ?? '',
          lowStockThreshold: props.initialValues?.lowStockThreshold ?? '',
          status: props.initialValues?.status || InventoryItemStatusEnum.ACTIVE,
          tags: props.initialValues?.tags ? props.initialValues?.tags.join(',') : '',
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              <FormikTextInput name={'name'} label={t('common.name')} />
              <ComboBox
                id={'inventory-item-category'}
                titleText={t('common.category')}
                items={(inventoryCategories?.data || []).map((item) => ({ id: item.id, text: item.name }))}
                selectedItem={
                  (inventoryCategories?.data || [])
                    .filter((item) => item.id === values.categoryId)
                    .map((item) => ({ id: item.id, text: item.name }))[0]
                }
                onChange={(e) => setFieldValue('categoryId', e.selectedItem?.id)}
                itemToString={(item) => (item ? item.text : '')}
              />
              <Row gap={1}>
                <ComboBox
                  id={'inventory-item-unit'}
                  titleText={t('common.unit')}
                  items={InventoryUnits}
                  selectedItem={InventoryUnits.find((item) => item.value === values.unit)}
                  onChange={(e) => setFieldValue('unit', e.selectedItem?.value)}
                  itemToString={(item) => (item ? t(item.label) : '')}
                />
                <MoneyInput name={'unitCost'} label={t('common.unitCost')} />
              </Row>
              <Row gap={1}>
                <FormikTextInput name={'lowStockThreshold'} isNumber label={t('common.lowStockThreshold')} />
                <FormikTextInput name={'tags'} label={`${t('common.tags')} (${t('common.separateByAComma')})`} />
              </Row>
              <FormikTextInput name={'description'} label={t('common.description')} />
              <FormikRadioButtonGroup
                name={'status'}
                id={'inventory-item-status-radio'}
                useTranslation
                label={t('common.status')}
                value={values.status!}
                onChange={(value) => setFieldValue('status', value)}
                options={InventoryItemStatuses}
              />
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(InventoryItemForm);
